import { OrganizationAction } from './actions';
import * as ActionType from './constants';

const INIT_STATE = {
  details: null,
  detailsLoading: false,
  detailsError: null,
  successMessage: null,
  failureMessage: null,
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
  listClients: null,
  listClientsLoading: false,
  listClientsError: null,
  mainClientProfileLoading: false,
  mainClientProfileError: null,
  mainClientProfile: null,
  savingMainClient: false,
  listUsers: null,
  listUsersLoading: false,
  listUsersError: null,
  performingAction: false,
  actionSuccessMessage: null,
  actionFailureMessage: null,
  vpList: null,
  vpListLoading: null,
  vpListFailureMessage: null,
  vpDetailsDict: {},
  vpDetailsLoading: null,
  vpDetailsFailureMessage: null,
  reassignLoading: false,
  reassignSuccessMessage: null,
  reassignFailureMessage: null,
  reassignResult: null,
  licensesActionLoading: false,
  licensesActionSuccessMessage: false,
  licensesActionFailureMessage: false,
  license: null,
  invitations: null,
  invitationsLoading: false,
  invitationsError: null,
  message: '',
  isSuccess: false,
  organizationInvitationTokenDetails: null,
  organizationInvitationTokenDetailsLoading: false,
  organizationInvitationTokenDetailsError: null,
  declineOrganizationInvitationLoading: false,
  declineOrganizationInvitationMessage: '',
  declineOrganizationInvitationSuccess: false,
  showTransferConfirmation: false,
  transferredVPsCount: 0,
  transferredClientsCount: 0,
  ownerCheck: null,
  ownerCheckLoading: false,
  ownerCheckError: null,
  transferOwnershipLoading: false,
  transferOwnershipError: null,
  transferOwnershipSuccess: null,
  deactivateOrganizationLoading: false,
  deactivateOrganizationError: null,
  deactivateOrganizationSuccess: null,
  recoveryCode: null,
  sendingRecoveryCodeEmail: false,
  sendRecoveryCodeEmailError: null,
  sendRecoveryCodeEmailSuccess: null,
  generatingRecoveryCodeAfterUnsubscribe: false,
  generateRecoveryCodeAfterUnsubscribeError: null,
  reactivatingOrganization: false,
  reactivateOrganizationError: null,
  reactivateOrganizationSuccess: null,
  reactivatedOrganizationId: null,
  reactivatedOrganizationName: null,
};

type State = {
  details?: {},
  detailsLoading: boolean,
  detailsError?: string,
  successMessage?: string,
  failureMessage?: string,
  dashboard?: {},
  dashboardLoading: boolean,
  dashboardError?: string,
  listClients?: {},
  listClientsLoading: boolean,
  listClientsError?: string,
  mainClientProfileLoading: boolean,
  mainClientProfileError?: string,
  mainClientProfile?: {},
  savingMainClient: boolean,
  listUsers?: {},
  listUsersLoading: boolean,
  listUsersError?: string,
  performingAction: boolean,
  actionSuccessMessage?: string,
  actionFailureMessage?: string,
  vpList?: {},
  vpListLoading?: boolean,
  vpListFailureMessage?: boolean,
  vpDetailsDict: {},
  vpDetailsLoading?: boolean,
  vpDetailsFailureMessage?: boolean,
  licensesActionLoading: boolean,
  licensesActionSuccessMessage?: string,
  licensesActionFailureMessage?: string,
  license: boolean,
  invitations?: {},
  invitationsLoading: boolean,
  invitationsError?: string,
  message: '',
  isSuccess: false,
  organizationInvitationTokenDetails?: ?{},
  organizationInvitationTokenDetailsLoading?: boolean,
  organizationInvitationTokenDetailsError?: ?string,
  declineOrganizationInvitationLoading?: boolean,
  declineOrganizationInvitationMessage?: string,
  declineOrganizationInvitationSuccess?: boolean,
  showTransferConfirmation?: boolean,
  transferredVPsCount?: number,
  transferredClientsCount?: number,
  ownerCheck?: {},
  ownerCheckLoading?: boolean,
  ownerCheckError?: string,
  transferOwnershipLoading?: boolean,
  transferOwnershipError?: string,
  transferOwnershipSuccess?: string,
  deactivateOrganizationLoading?: boolean,
  deactivateOrganizationError?: string,
  deactivateOrganizationSuccess?: string,
  recoveryCode?: string,
  reactivatingOrganization?: boolean,
  reactivateOrganizationError?: string,
  reactivateOrganizationSuccess?: string,
  reactivatedOrganizationId?: number,
  reactivatedOrganizationName?: string,
};

const Organization = (state: State = INIT_STATE, action: OrganizationAction) => {
  switch (action.type) {
    case ActionType.CLEAR_ORGANIZATION_MESSAGES:
      return {
        ...state,
        detailsError: null,
        successMessage: null,
        failureMessage: null,
        dashboardError: null,
        listUsersError: null,
        actionSuccessMessage: null,
        actionFailureMessage: null,
        licensesActionSuccessMessage: null,
        licensesActionFailureMessage: null,
        recoveryCode: null,
        sendingRecoveryCodeEmail: false,
        sendRecoveryCodeEmailError: null,
        sendRecoveryCodeEmailSuccess: null,
        showTransferConfirmation: false,
        transferredVPsCount: 0,
        transferredClientsCount: 0,
        deactivateOrganizationSuccess: null,
        deactivateOrganizationError: null,
        deactivateOrganizationLoading: false,
        generatingRecoveryCodeAfterUnsubscribe: false,
        recoveryCode: null,
      };

    case ActionType.LOAD_ORGANIZATION_DETAILS:
      return { ...state, detailsLoading: true, detailsError: null };
    case ActionType.LOAD_ORGANIZATION_DETAILS_SUCCESS:
      return { ...state, detailsLoading: false, details: action.payload };
    case ActionType.LOAD_ORGANIZATION_DETAILS_FAILED:
      return { ...state, detailsLoading: false, detailsError: action.payload, details: null };

    case ActionType.EDIT_ORGANIZATION_DETAILS:
      return {
        ...state,
        detailsLoading: true,
        detailsError: null,
        successMessage: null,
        failureMessage: null,
      };
    case ActionType.EDIT_ORGANIZATION_DETAILS_SUCCESS:
      return {
        ...state,
        detailsLoading: false,
        details: null,
        successMessage: action?.payload?.successMessage,
      };
    case ActionType.EDIT_ORGANIZATION_DETAILS_FAILED:
      return { ...state, detailsLoading: false, failureMessage: action.payload };

    case ActionType.CREATE_ORGANIZATION:
      return { 
        ...state, 
        detailsLoading: true, 
        successMessage: null, 
        failureMessage: null,
        showTransferConfirmation: false,
        transferredVPsCount: 0,
        transferredClientsCount: 0,
      };
    case ActionType.CREATE_ORGANIZATION_SUCCESS:
      return { 
        ...state, 
        detailsLoading: false, 
        successMessage: action.payload.successMessage,
        showTransferConfirmation: action.payload.showConfirmation || false,
        transferredVPsCount: action.payload.transferredVPsCount || 0,
        transferredClientsCount: action.payload.transferredClientsCount || 0,
      };
    case ActionType.CREATE_ORGANIZATION_FAILED:
      return { ...state, detailsLoading: false, failureMessage: action.payload };

    case ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA:
      return { ...state, dashboardLoading: true, dashboardError: null };
    case ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA_SUCCESS:
      return {
        ...state,
        dashboardLoading: false,
        dashboard: {
          isOwner: action.payload.isOwner,
          vpoTotalImplementation: action.payload.vpoTotalImplementation,
          vpoTotalForecast: action.payload.vpoTotalForecast,
          vpoTotalProposition: action.payload.vpoTotalProposition,
          valuePropositionSummary: action.payload.valuePropositionSummary,
          totalClients: action.payload.totalClients,
          totalValueProposition: action.payload.totalValueProposition,
          totalPortifolioAmount: action.payload.totalPortifolioAmount,
          totalFundamentals: action.payload.totalFundamentals,
          usersByPlan: action.payload.usersByPlan || {},
        },
      };
    case ActionType.LOAD_ORGANIZATION_DASHBOARD_DATA_FAILED:
      return { ...state, dashboardLoading: false, dashboardError: action.payload };

    case ActionType.LOAD_ORGANIZATION_CLIENTS:
      return { ...state, listClientsLoading: true, listClientsError: null, listClients: null };
    case ActionType.LOAD_ORGANIZATION_CLIENTS_SUCCESS:
      return { ...state, listClientsLoading: false, listClients: action.payload };
    case ActionType.LOAD_ORGANIZATION_CLIENTS_FAILED:
      return { ...state, listClientsLoading: false, listClientsError: action.payload };
    case ActionType.LOAD_ORGANIZATION_MAIN_CLIENT:
      return {
        ...state,
        mainClientProfileLoading: true,
        mainClientProfileError: null,
        mainClientProfile: null,
      };
    case ActionType.LOAD_ORGANIZATION_MAIN_CLIENT_SUCCESS:
      return {
        ...state,
        mainClientProfileLoading: false,
        mainClientProfile: action.payload.mainClientProfile,
      };
    case ActionType.LOAD_ORGANIZATION_MAIN_CLIENT_FAILED:
      return { ...state, mainClientProfileLoading: false, mainClientProfileError: action.payload };
    case ActionType.SAVE_ORGANIZATION_MAIN_CLIENT:
      return { ...state, savingMainClient: true, mainClientProfileError: null };
    case ActionType.SAVE_ORGANIZATION_MAIN_CLIENT_SUCCESS:
      return { ...state, savingMainClient: false, mainClientProfile: action.payload };
    case ActionType.SAVE_ORGANIZATION_MAIN_CLIENT_FAILED:
      return { ...state, savingMainClient: false, mainClientProfileError: action.payload };

    case ActionType.LOAD_ORGANIZATION_USERS:
      return { ...state, listUsersLoading: true, listUsersError: null };
    case ActionType.LOAD_ORGANIZATION_USERS_SUCCESS:
      return { ...state, listUsersLoading: false, listUsers: action.payload };
    case ActionType.LOAD_ORGANIZATION_USERS_FAILED:
      return { ...state, listUsersLoading: false, listUsersError: action.payload };

    case ActionType.INVITE_USER_TO_ORGANIZATION:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.INVITE_USER_TO_ORGANIZATION_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.INVITE_USER_TO_ORGANIZATION_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.RESEND_ORGANIZATION_INVITATION:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.RESEND_ORGANIZATION_INVITATION_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.RESEND_ORGANIZATION_INVITATION_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.CANCEL_ORGANIZATION_INVITATION:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.CANCEL_ORGANIZATION_INVITATION_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.CANCEL_ORGANIZATION_INVITATION_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.MAKE_USER_MANAGER:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.MAKE_USER_MANAGER_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.MAKE_USER_MANAGER_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.REMOVE_USER_MANAGER_FLAG:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.REMOVE_USER_MANAGER_FLAG_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.REMOVE_USER_MANAGER_FLAG_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.ASSOCIATE_LICENSE:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.ASSOCIATE_LICENSE_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.ASSOCIATE_LICENSE_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.CANCEL_LICENSE:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.CANCEL_LICENSE_SUCCESS:
      return { ...state, performingAction: false, actionSuccessMessage: action.payload };
    case ActionType.CANCEL_LICENSE_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };

    case ActionType.REMOVE_USER_FROM_ORGANIZATION:
      return {
        ...state,
        performingAction: true,
        actionSuccessMessage: null,
        actionFailureMessage: null,
      };
    case ActionType.REMOVE_USER_FROM_ORGANIZATION_SUCCESS:
      return { 
        ...state, 
        performingAction: false, 
        actionSuccessMessage: action.payload.successMessage || action.payload,
        recoveryCode: action.payload.recoveryCode || null,
        details: null, // Clear organization details when user unsubscribes
      };
    case ActionType.REMOVE_USER_FROM_ORGANIZATION_FAILED:
      return { ...state, performingAction: false, actionFailureMessage: action.payload };
    case ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS:
      return {
        ...state,
        vpDetailsDict: {},
        vpList: null,
        vpListLoading: true,
        vpListFailureMessage: null,
      };
    case ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS_SUCCESS:
      return { ...state, vpListLoading: false, vpList: action.payload };
    case ActionType.LOAD_ORGANIZATION_VALUE_PROPOSITIONS_FAILED:
      return { ...state, vpListLoading: false, vpListFailureMessage: action.payload };

    case ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS:
      return { ...state, vpDetailsLoading: true, vpDetailsFailureMessage: null };
    case ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS_SUCCESS:
      return {
        ...state,
        vpDetailsLoading: false,
        vpDetailsDict: { ...state.vpDetailsDict, [action.payload.vpId]: action.payload.vpDetails },
      };
    case ActionType.GET_ORGANIZATION_VALUE_PROPOSITION_DETAILS_FAILED:
      return { ...state, vpDetailsLoading: false, vpDetailsFailureMessage: action.payload };

    case ActionType.REASSIGN_VALUE_PROPOSITION:
    case ActionType.REASSIGN_VALUE_PROPOSITIONS:
      return {
        ...state,
        reassignLoading: true,
        reassignSuccessMessage: null,
        reassignFailureMessage: null,
        reassignResult: null,
      };
    case ActionType.REASSIGN_VALUE_PROPOSITION_SUCCESS:
      return { ...state, reassignLoading: false, reassignSuccessMessage: action.payload };
    case ActionType.REASSIGN_VALUE_PROPOSITION_FAILED:
      return { ...state, reassignLoading: false, reassignFailureMessage: action.payload };
    case ActionType.REASSIGN_VALUE_PROPOSITIONS_SUCCESS:
      return {
        ...state,
        reassignLoading: false,
        reassignResult: action.payload,
        reassignSuccessMessage: action.payload?.message ?? null,
      };
    case ActionType.REASSIGN_VALUE_PROPOSITIONS_FAILED:
      return {
        ...state,
        reassignLoading: false,
        reassignResult: action.payload,
        reassignFailureMessage: action.payload?.message ?? null,
      };
    case ActionType.CLEAR_REASSIGN_STATE:
      return {
        ...state,
        reassignLoading: false,
        reassignSuccessMessage: null,
        reassignFailureMessage: null,
        reassignResult: null,
      };

    case ActionType.ORGANIZATION_UPGRADE_LICENSES:
    case ActionType.ORGANIZATION_DOWNGRADE_LICENSES:
    case ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE:
      return {
        ...state,
        licensesActionLoading: true,
        licensesActionSuccessMessage: null,
        licensesActionFailureMessage: null,
      };
    case ActionType.ORGANIZATION_UPGRADE_LICENSES_SUCCESS:
    case ActionType.ORGANIZATION_DOWNGRADE_LICENSES_SUCCESS:
    case ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE_SUCCESS:
      return {
        ...state,
        licensesActionLoading: false,
        licensesActionSuccessMessage: action.payload,
        details: null,
      };
    case ActionType.ORGANIZATION_UPGRADE_LICENSES_FAILED:
    case ActionType.ORGANIZATION_DOWNGRADE_LICENSES_FAILED:
    case ActionType.ORGANIZATION_USER_LICENSE_SUCCESS_STRIPE_FAILED:
      return {
        ...state,
        licensesActionLoading: false,
        licensesActionFailureMessage: action.payload,
      };

    case ActionType.CHANGE_ORGANIZATION_USER_LICENSES:
      return {
        ...state,
        licensesActionLoading: true,
        licensesActionFailureMessage: null,
        license: null,
      };
    case ActionType.CHANGE_ORGANIZATION_USER_LICENSES_SUCCESS:
      return { ...state, licensesActionLoading: false, license: action.payload };
    case ActionType.CHANGE_ORGANIZATION_USER_LICENSES_FAILED:
      return {
        ...state,
        licensesActionLoading: false,
        licensesActionFailureMessage: action.payload,
      };

    case ActionType.CLEAR_ORGANIZATION_USER_LICENSE:
      return { ...state, license: null };

    case ActionType.GET_MY_INVITATIONS:
      return { ...state, invitationsLoading: true, invitationsError: null, invitations: null };
    case ActionType.GET_MY_INVITATIONS_SUCCESS:
      return { ...state, invitationsLoading: false, invitations: action.payload };
    case ActionType.GET_MY_INVITATIONS_FAILED:
      return { ...state, invitationsLoading: false, invitationsError: action.payload };
    case ActionType.ACCEPT_INVITATION:
      return { ...state, invitationsLoading: true, invitationsError: null };
    case ActionType.ACCEPT_INVITATION_SUCCESS:
      const newInvitationsAfterSuccess = state.invitations.filter(
        (invite) => invite.organizationId !== action.payload.organizationId
      );
      return {
        ...state,
        invitationsLoading: false,
        invitations: newInvitationsAfterSuccess,
        successMessage: action.payload.successMessage,
        organizationId: action.payload.organizationId,
      };
    case ActionType.ACCEPT_INVITATION_FAILED:
      return { ...state, invitationsLoading: false, invitationsError: action.payload };

    case ActionType.ACCEPT_ORGANIZATION_INVITATION:
      return { ...state, message: 'Loading', isSuccess: false };
    case ActionType.ACCEPT_ORGANIZATION_INVITATION_SUCCESS:
      return {
        ...state,
        message: action.payload.message,
        isSuccess: action.payload.isSuccess,
      };
    case ActionType.ACCEPT_ORGANIZATION_INVITATION_FAILED:
      return {
        ...state,
        message: action.payload.message,
        isSuccess: action.payload.isSuccess,
      };

    case ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS:
      return {
        ...state,
        organizationInvitationTokenDetailsLoading: true,
        organizationInvitationTokenDetailsError: null,
        organizationInvitationTokenDetails: null,
        message: '',
        isSuccess: false,
        declineOrganizationInvitationLoading: false,
        declineOrganizationInvitationMessage: '',
        declineOrganizationInvitationSuccess: false,
      };
    case ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS_SUCCESS:
      return {
        ...state,
        organizationInvitationTokenDetailsLoading: false,
        organizationInvitationTokenDetails: action.payload,
        organizationInvitationTokenDetailsError: null,
      };
    case ActionType.LOAD_ORGANIZATION_INVITATION_TOKEN_DETAILS_FAILED:
      return {
        ...state,
        organizationInvitationTokenDetailsLoading: false,
        organizationInvitationTokenDetails: null,
        organizationInvitationTokenDetailsError: action.payload,
      };

    case ActionType.DECLINE_ORGANIZATION_INVITATION:
      return {
        ...state,
        declineOrganizationInvitationLoading: true,
        declineOrganizationInvitationMessage: '',
        declineOrganizationInvitationSuccess: false,
      };
    case ActionType.DECLINE_ORGANIZATION_INVITATION_SUCCESS:
      return {
        ...state,
        declineOrganizationInvitationLoading: false,
        declineOrganizationInvitationMessage: action.payload.message,
        declineOrganizationInvitationSuccess: action.payload.isSuccess,
      };
    case ActionType.DECLINE_ORGANIZATION_INVITATION_FAILED:
      return {
        ...state,
        declineOrganizationInvitationLoading: false,
        declineOrganizationInvitationMessage: action.payload.message,
        declineOrganizationInvitationSuccess: action.payload.isSuccess,
      };

    case ActionType.REJECT_INVITATION:
      return { ...state, invitationsLoading: true, invitationsError: null };
    case ActionType.REJECT_INVITATION_SUCCESS:
      const newInvitationsAfterReject = state.invitations.filter(
        (invite) => invite.organizationId !== action.payload.organizationId
      );
      return {
        ...state,
        invitationsLoading: false,
        invitations: newInvitationsAfterReject,
        successMessage: action.payload.successMessage,
        organizationId: null,
      };
    case ActionType.REJECT_INVITATION_FAILED:
      return { ...state, invitationsLoading: false, invitationsError: action.payload };

    case ActionType.CHECK_OWNER_AND_GET_MANAGERS:
      return { ...state, ownerCheckLoading: true, ownerCheckError: null };
    case ActionType.CHECK_OWNER_AND_GET_MANAGERS_SUCCESS:
      return {
        ...state,
        ownerCheckLoading: false,
        ownerCheck: action.payload,
        ownerCheckError: null,
      };
    case ActionType.CHECK_OWNER_AND_GET_MANAGERS_FAILED:
      return {
        ...state,
        ownerCheckLoading: false,
        ownerCheckError: action.payload,
      };

    case ActionType.TRANSFER_OWNERSHIP:
      return {
        ...state,
        transferOwnershipLoading: true,
        transferOwnershipError: null,
        transferOwnershipSuccess: null,
      };
    case ActionType.TRANSFER_OWNERSHIP_SUCCESS:
      return {
        ...state,
        transferOwnershipLoading: false,
        transferOwnershipSuccess: action.payload.message,
        transferOwnershipError: null,
      };
    case ActionType.TRANSFER_OWNERSHIP_FAILED:
      return {
        ...state,
        transferOwnershipLoading: false,
        transferOwnershipError: action.payload,
      };

    case ActionType.DEACTIVATE_ORGANIZATION:
      return {
        ...state,
        deactivateOrganizationLoading: true,
        deactivateOrganizationError: null,
        deactivateOrganizationSuccess: null,
        recoveryCode: null,
      };
    case ActionType.DEACTIVATE_ORGANIZATION_SUCCESS:
      return {
        ...state,
        deactivateOrganizationLoading: false,
        deactivateOrganizationSuccess: action.payload.message,
        recoveryCode: action.payload.recoveryCode,
        deactivateOrganizationError: null,
      };
    case ActionType.DEACTIVATE_ORGANIZATION_FAILED:
      return {
        ...state,
        deactivateOrganizationLoading: false,
        deactivateOrganizationError: action.payload,
      };

    case ActionType.SEND_RECOVERY_CODE_EMAIL:
      return {
        ...state,
        sendingRecoveryCodeEmail: true,
        sendRecoveryCodeEmailError: null,
      };
    case ActionType.SEND_RECOVERY_CODE_EMAIL_SUCCESS:
      return {
        ...state,
        sendingRecoveryCodeEmail: false,
        sendRecoveryCodeEmailError: null,
        sendRecoveryCodeEmailSuccess: action.payload,
      };
    case ActionType.SEND_RECOVERY_CODE_EMAIL_FAILED:
      return {
        ...state,
        sendingRecoveryCodeEmail: false,
        sendRecoveryCodeEmailError: action.payload,
      };

    case ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE:
      return {
        ...state,
        generatingRecoveryCodeAfterUnsubscribe: true,
        generateRecoveryCodeAfterUnsubscribeError: null,
      };
    case ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE_SUCCESS:
      return {
        ...state,
        generatingRecoveryCodeAfterUnsubscribe: false,
        recoveryCode: action.payload.recoveryCode,
        generateRecoveryCodeAfterUnsubscribeError: null,
      };
    case ActionType.GENERATE_RECOVERY_CODE_AFTER_UNSUBSCRIBE_FAILED:
      return {
        ...state,
        generatingRecoveryCodeAfterUnsubscribe: false,
        generateRecoveryCodeAfterUnsubscribeError: action.payload,
      };

    case ActionType.REACTIVATE_ORGANIZATION:
      return {
        ...state,
        reactivatingOrganization: true,
        reactivateOrganizationError: null,
        reactivateOrganizationSuccess: null,
      };
    case ActionType.REACTIVATE_ORGANIZATION_SUCCESS:
      return {
        ...state,
        reactivatingOrganization: false,
        reactivateOrganizationError: null,
        reactivateOrganizationSuccess: action.payload.message,
        reactivatedOrganizationId: action.payload.organizationId,
        reactivatedOrganizationName: action.payload.organizationName,
      };
    case ActionType.REACTIVATE_ORGANIZATION_FAILED:
      return {
        ...state,
        reactivatingOrganization: false,
        reactivateOrganizationError: action.payload,
        reactivateOrganizationSuccess: null,
      };

    default:
      return { ...state };
  }
};

export default Organization;
